import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  completeTreasuryPeriod,
  getTreasuryPeriod,
  getTreasuryPeriodReport,
} from "../api/cierresTesoreriaApi";
import CajaCierreTable from "../components/CajaCierreTable";
import CierresSection from "../components/CierresSection";
import ConfirmDeleteClosureModal from "../components/ConfirmDeleteClosureModal";
import LienzoFilters from "../components/LienzoFilters";
import LienzoTabs from "../components/LienzoTabs";
import MovimientosCierreTable from "../components/MovimientosCierreTable";
import useTesoreriaCierres from "../hooks/useTesoreriaCierres";
import CierreResumen from "../../historial/components/CierreResumen";

const CURRENT_CLOSURE_KEY = "sabi_tesoreria_cierre_actual";
const VALID_TABS = ["closures", "summary", "movements", "cash"];

function Pill({ children, sx }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.1,
        bgcolor: "secondary.50",
        borderRadius: "33px",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function errorToMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.detail ||
    error?.data?.message ||
    error?.message ||
    "No se pudo completar la operación."
  );
}

function formatDate(value) {
  if (!value) return "-";
  const [year, month, day] = String(value).split("T")[0].split("-");
  return `${day || "--"}/${month || "--"}/${year || "----"}`;
}

function normalizeTab(value, hasActiveClosure) {
  if (!hasActiveClosure) return "closures";
  return VALID_TABS.includes(value) ? value : "movements";
}

function getClosureMode(closure) {
  return closure?.status === "DRAFT" ? "edit" : "readonly";
}

function saveCurrentClosure(closure) {
  const context = {
    closureId: closure.id,
    status: closure.status,
    mode: getClosureMode(closure),
    periodStart: closure.periodStart,
    periodEnd: closure.periodEnd,
  };

  try {
    sessionStorage.setItem(CURRENT_CLOSURE_KEY, JSON.stringify(context));
  } catch {
    // La URL sigue siendo la fuente de verdad del contexto activo.
  }

  return context;
}

function clearCurrentClosure() {
  try {
    sessionStorage.removeItem(CURRENT_CLOSURE_KEY);
  } catch {
    //
  }
}

function ActiveClosureContext({ closure }) {
  if (!closure) return null;

  const isDraft = closure.status === "DRAFT";
  const statusLabel = isDraft ? "En trabajo" : "Completado";
  const title = `Cierre ${formatDate(closure.periodStart)} - ${formatDate(closure.periodEnd)}`;

  return (
    <Box
      sx={{
        px: { xs: 1.4, sm: 1.8 },
        py: 1.2,
        width: { xs: "100%", sm: "fit-content" },
        borderRadius: "16px",
        bgcolor: "rgba(244, 245, 251, 0.88)",
        border: "1px solid rgba(255,255,255,0.8)",
        boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.18)",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: "neutral.600", mb: 0.2 }}>
            Cierre seleccionado
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 900, color: "primary.900", lineHeight: 1.2 }}>
            {title}
          </Typography>
        </Box>

        <Chip
          label={statusLabel}
          size="small"
          sx={{
            borderRadius: "999px",
            bgcolor: isDraft ? "secondary.100" : "rgba(185, 246, 202, 0.72)",
            color: "primary.900",
            fontSize: 12,
            fontWeight: 900,
          }}
        />
      </Stack>
    </Box>
  );
}

function ClosureStatusTabs({ value, draftCount, completedCount, onChange }) {
  const tabIndex = value === "draft" ? 0 : 1;

  return (
    <Tabs
      value={tabIndex}
      onChange={(event, newValue) => onChange(newValue === 0 ? "draft" : "completed")}
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        mb: 1,
        "& .MuiTab-root": {
          fontWeight: 700,
          textTransform: "none",
          fontSize: 15,
          color: "neutral.700",
        },
        "& .Mui-selected": {
          color: "primary.900",
        },
        "& .MuiTabs-indicator": {
          bgcolor: "primary.900",
          height: 3,
          borderRadius: "3px 3px 0 0",
        },
      }}
    >
      <Tab label={`Cierres en trabajo (${draftCount})`} />
      <Tab label={`Cierres completados (${completedCount})`} />
    </Tabs>
  );
}

function ConfirmPendingChangesModal({ open, onCancel, onConfirm }) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      PaperProps={{ sx: { borderRadius: "18px", maxWidth: 440 } }}
    >
      <DialogTitle sx={{ fontSize: 18, fontWeight: 900, color: "primary.900" }}>
        Cambios sin guardar
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "neutral.700" }}>
          Hay cambios pendientes sin guardar. Para cambiar de cierre o salir de la pestaña,
          primero confirmá que querés descartarlos.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onCancel}
          sx={{
            color: "primary.900",
            borderRadius: "24px",
            textTransform: "none",
            fontWeight: 800,
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          sx={{
            bgcolor: "primary.900",
            color: "white",
            px: 2,
            borderRadius: "24px",
            textTransform: "none",
            fontWeight: 800,
            "&:hover": { bgcolor: "primary.800" },
          }}
        >
          Descartar y continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function TesoreriaLienzoTrabajoPage() {
  const navigate = useNavigate();
  const { periodId } = useParams();
  const [searchParams] = useSearchParams();

  const requestedTab = searchParams.get("tab");
  const activeTab = normalizeTab(requestedTab, Boolean(periodId));

  const [filters, setFilters] = useState({ search: "", dateFrom: "", dateTo: "" });
  const [currentClosure, setCurrentClosure] = useState(null);
  const [loadingClosure, setLoadingClosure] = useState(false);
  const [closureError, setClosureError] = useState("");
  const [closureToDelete, setClosureToDelete] = useState(null);
  const [movementsDirty, setMovementsDirty] = useState(false);
  const [cashDirty, setCashDirty] = useState(false);
  const [closureListStatus, setClosureListStatus] = useState("draft");
  const [completingClosure, setCompletingClosure] = useState(false);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState("");
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const dateError = useMemo(() => {
    if (filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo) {
      return "La fecha desde no puede ser posterior a la fecha hasta.";
    }
    return "";
  }, [filters.dateFrom, filters.dateTo]);

  const queryFilters = dateError ? { search: filters.search, dateFrom: "", dateTo: "" } : filters;

  const {
    draftClosures,
    completedClosures,
    loading,
    error,
    deletingId,
    successMessage,
    setSuccessMessage,
    refetch,
    removeDraftClosure,
    updateClosure,
  } = useTesoreriaCierres(queryFilters);

  const showToast = (severity, message) => {
    setToast({ open: true, severity, message });
  };

  const handleMovementsSaved = ({ periodId: savedPeriodId, derivedData }) => {
    if (derivedData) {
      setCurrentClosure((prev) =>
        prev && String(prev.id) === String(savedPeriodId)
          ? { ...prev, ...derivedData }
          : prev
      );
      updateClosure(savedPeriodId, derivedData);
      return;
    }

    getTreasuryPeriod(savedPeriodId)
      .then((refreshedClosure) => {
        setCurrentClosure((prev) =>
          prev && String(prev.id) === String(savedPeriodId)
            ? refreshedClosure
            : prev
        );
        updateClosure(savedPeriodId, refreshedClosure);
      })
      .catch(() => {
        showToast(
          "warning",
          "Los movimientos se guardaron, pero no se pudieron actualizar los totales."
        );
      });
  };

  useEffect(() => {
    if (!periodId) {
      setCurrentClosure(null);
      setClosureError("");
      setLoadingClosure(false);
      setMovementsDirty(false);
      setCashDirty(false);
      clearCurrentClosure();
      return;
    }

    let alive = true;

    (async () => {
      setLoadingClosure(true);
      setClosureError("");

      try {
        const closure = await getTreasuryPeriod(periodId);
        if (!alive) return;
        setCurrentClosure(closure);
        saveCurrentClosure(closure);
      } catch (err) {
        if (!alive) return;
        setCurrentClosure(null);
        setClosureError(errorToMessage(err));
      } finally {
        if (alive) setLoadingClosure(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [periodId]);

  useEffect(() => {
    if (!periodId || activeTab !== "summary") {
      setReport(null);
      setReportError("");
      setLoadingReport(false);
      return;
    }

    let alive = true;
    setLoadingReport(true);
    setReportError("");
    getTreasuryPeriodReport(periodId)
      .then((nextReport) => {
        if (alive) setReport(nextReport);
      })
      .catch((err) => {
        if (!alive) return;
        setReport(null);
        setReportError(errorToMessage(err));
      })
      .finally(() => {
        if (alive) setLoadingReport(false);
      });

    return () => {
      alive = false;
    };
  }, [activeTab, periodId]);

  useEffect(() => {
    if (!periodId) return;
    if (requestedTab && VALID_TABS.includes(requestedTab)) return;
    navigate(`/tesoreria/lienzo/${periodId}?tab=movements`, { replace: true });
  }, [navigate, periodId, requestedTab]);

  useEffect(() => {
    if (activeTab === "closures") {
      refetch();
    }
  }, [activeTab, refetch]);

  useEffect(() => {
    if (!movementsDirty && !cashDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [movementsDirty, cashDirty]);

  const hasPendingChanges =
    (activeTab === "movements" && movementsDirty) ||
    (activeTab === "cash" && cashDirty);

  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasPendingChanges &&
      `${currentLocation.pathname}${currentLocation.search}` !==
        `${nextLocation.pathname}${nextLocation.search}`
  );

  const goTo = (to, options = {}) => {
    navigate(to, options);
    return true;
  };

  const handleGenerateClosure = () => {
    goTo("/tesoreria/lienzo/generar");
  };

  const handleBack = () => {
    if (periodId) {
      goTo("/tesoreria/lienzo");
      return;
    }

    goTo("/tesoreria");
  };

  const handleTabChange = (nextTab) => {
    if (!periodId) return;
    goTo(`/tesoreria/lienzo/${periodId}?tab=${nextTab}`);
  };

  const handleContinue = (closure) => {
    const navigated = goTo(`/tesoreria/lienzo/${closure.id}?tab=movements`);
    if (navigated && String(closure.id) !== String(periodId)) {
      showToast("success", "Cierre abierto como período actual.");
    }
  };

  const handleViewMovements = (closure) => {
    goTo(`/tesoreria/lienzo/${closure.id}?tab=movements`);
  };

  const handleViewSummary = (closure) => {
    goTo(`/tesoreria/lienzo/${closure.id}?tab=summary`);
  };

  const handleViewCash = (closure) => {
    const navigated = goTo(`/tesoreria/lienzo/${closure.id}?tab=cash`);
    if (navigated) {
      showToast("info", "Caja del Cierre queda asociada al período seleccionado.");
    }
  };

  const handleSelectCategory = (movementType, category) => {
    const categoryId = category.categoryId ?? "__uncategorized__";
    const params = new URLSearchParams({
      tab: "movements",
      movementType,
      categoryId: String(categoryId),
    });
    goTo(`/tesoreria/lienzo/${periodId}?${params.toString()}`);
  };

  const handleConfirmPendingNavigation = () => {
    if (navigationBlocker.state !== "blocked") return;
    setMovementsDirty(false);
    setCashDirty(false);
    navigationBlocker.proceed();
  };

  const handleConfirmDelete = async () => {
    if (!closureToDelete) return;

    try {
      await removeDraftClosure(closureToDelete.id);
      if (String(closureToDelete.id) === String(periodId)) {
        clearCurrentClosure();
        goTo("/tesoreria/lienzo");
      }
      setClosureToDelete(null);
    } catch (err) {
      showToast("error", errorToMessage(err) || "No se pudo eliminar el cierre.");
    }
  };

  const handleCompleteClosure = async (closureId) => {
    if (completingClosure) return;

    setCompletingClosure(true);
    try {
      const completed = await completeTreasuryPeriod(closureId);
      if (completed.status !== "COMPLETED") {
        throw new Error("El backend no confirmó el cierre completado.");
      }

      setCurrentClosure(completed);
      saveCurrentClosure(completed);
      setMovementsDirty(false);
      await refetch();
      setClosureListStatus("completed");
      navigate("/tesoreria/lienzo?tab=closures");
      showToast("success", "Cierre completado correctamente.");
    } catch (err) {
      showToast("error", errorToMessage(err));
      throw err;
    } finally {
      setCompletingClosure(false);
    }
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
    setSuccessMessage("");
  };

  const showClosureTabs = Boolean(periodId && currentClosure && !loadingClosure && !closureError);
  const currentMode = getClosureMode(currentClosure);
  const isReadonly = currentMode === "readonly";
  const showActiveClosureContext = showClosureTabs && activeTab === "closures";
  const visibleClosures = closureListStatus === "draft" ? draftClosures : completedClosures;
  const visibleClosureConfig =
    closureListStatus === "draft"
      ? {
          emptyMessage: "No hay cierres en trabajo.",
          variant: "draft",
        }
      : {
          emptyMessage: "No hay cierres completados.",
          variant: "completed",
        };
  const movementInitialFilters = useMemo(() => {
    const movementType = searchParams.get("movementType") || "";
    const categoryId = searchParams.get("categoryId") || "";
    return {
      movementType,
      categoryId,
      status: movementType && categoryId ? "confirmed" : "",
    };
  }, [searchParams]);
  const closureListContent = (
    <>
      <LienzoFilters
        filters={filters}
        onChange={setFilters}
        dateError={dateError}
        compact
      />

      <ClosureStatusTabs
        value={closureListStatus}
        draftCount={draftClosures.length}
        completedCount={completedClosures.length}
        onChange={setClosureListStatus}
      />

      <CierresSection
        emptyMessage={visibleClosureConfig.emptyMessage}
        closures={visibleClosures}
        variant={visibleClosureConfig.variant}
        loading={loading}
        error={error || dateError}
        deletingId={deletingId}
        onContinue={handleContinue}
        onDelete={setClosureToDelete}
        onViewSummary={handleViewSummary}
        onViewMovements={handleViewMovements}
        onViewCash={handleViewCash}
      />
    </>
  );
  const usesInnerWorkspaceCard =
    ["summary", "movements", "cash"].includes(activeTab) || showActiveClosureContext;

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
          <IconButton
            onClick={handleBack}
            sx={{
              width: 36,
              height: 36,
              bgcolor: "secondary.500",
              "&:hover": { bgcolor: "secondary.600" },
            }}
          >
            <ArrowBackIcon sx={{ color: "logoblanco", fontSize: 32 }} />
          </IconButton>

          <Stack sx={{ flex: 1, alignItems: "center", pr: "48px" }}>
            <Pill sx={{ bgcolor: "secondary.200" }}>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: "primary.900" }}>
                Lienzo de Trabajo
              </Typography>
            </Pill>
          </Stack>
        </Stack>

        {!periodId ? (
          <Stack direction="row" justifyContent="flex-end">
            <Button
              onClick={handleGenerateClosure}
              startIcon={<AddIcon />}
              sx={{
                bgcolor: "#b9f6ca",
                color: "primary.900",
                px: 2.5,
                py: 1,
                borderRadius: "24px",
                border: "2px solid",
                borderColor: "primary.900",
                textTransform: "none",
                fontWeight: 800,
                fontSize: 14,
                "&:hover": { bgcolor: "#a5f5bd" },
              }}
            >
              Generar cierre
            </Button>
          </Stack>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            p: usesInnerWorkspaceCard ? 0 : { xs: 1.5, sm: 2 },
            borderRadius: usesInnerWorkspaceCard ? 0 : "20px",
            bgcolor:
              usesInnerWorkspaceCard
                ? "transparent"
                : "rgba(255,255,255,0.86)",
            boxShadow:
              usesInnerWorkspaceCard
                ? "none"
                : "0 4px 12px rgba(0,0,0,0.05)",
            minHeight: { xs: 360, md: "calc(100vh - 188px)" },
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            {loadingClosure ? (
              <Stack alignItems="center" spacing={1.2} sx={{ py: 4 }}>
                <CircularProgress size={30} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900" }}>
                  Cargando cierre activo...
                </Typography>
              </Stack>
            ) : null}

            {!loadingClosure && closureError ? (
              <Alert severity="error" sx={{ borderRadius: "14px" }}>
                {closureError}
              </Alert>
            ) : null}

            {activeTab === "closures" ? (
              showActiveClosureContext ? (
                <>
                  <ActiveClosureContext closure={currentClosure} />
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: "20px",
                      bgcolor: "rgba(255,255,255,0.86)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      flex: 1,
                      minHeight: 0,
                    }}
                  >
                    <Stack spacing={2}>
                      <LienzoTabs
                        value={activeTab}
                        onChange={handleTabChange}
                        showClosureTabs={showClosureTabs}
                      />
                      {closureListContent}
                    </Stack>
                  </Paper>
                </>
              ) : (
                <>
                  <LienzoTabs
                    value={activeTab}
                    onChange={handleTabChange}
                    showClosureTabs={showClosureTabs}
                  />
                  {closureListContent}
                </>
              )
            ) : null}

            {activeTab === "summary" && showClosureTabs ? (
              <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                <ActiveClosureContext closure={currentClosure} />
                <CierreResumen
                  report={report}
                  loading={loadingReport}
                  error={reportError}
                  navigation={
                    <LienzoTabs
                      value={activeTab}
                      onChange={handleTabChange}
                      showClosureTabs={showClosureTabs}
                    />
                  }
                  onSelectCategory={handleSelectCategory}
                  statusLabel={isReadonly ? "Modo lectura" : "Resumen en vivo"}
                />
              </Stack>
            ) : null}

            {activeTab === "movements" && showClosureTabs ? (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <MovimientosCierreTable
                  period={currentClosure}
                  readonly={isReadonly}
                  completing={completingClosure}
                  onComplete={handleCompleteClosure}
                  onDirtyChange={setMovementsDirty}
                  onSaved={handleMovementsSaved}
                  initialFilters={movementInitialFilters}
                  navigation={
                    <LienzoTabs
                      value={activeTab}
                      onChange={handleTabChange}
                      showClosureTabs={showClosureTabs}
                    />
                  }
                />
              </Box>
            ) : null}

            {activeTab === "cash" && showClosureTabs ? (
              <CajaCierreTable
                period={currentClosure}
                readonly={isReadonly}
                completing={completingClosure}
                onComplete={handleCompleteClosure}
                onDirtyChange={setCashDirty}
                navigation={
                  <LienzoTabs
                    value={activeTab}
                    onChange={handleTabChange}
                    showClosureTabs={showClosureTabs}
                  />
                }
              />
            ) : null}
          </Stack>
        </Paper>
      </Stack>

      <ConfirmDeleteClosureModal
        open={Boolean(closureToDelete)}
        closure={closureToDelete}
        deleting={Boolean(deletingId)}
        onCancel={() => setClosureToDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      <ConfirmPendingChangesModal
        open={navigationBlocker.state === "blocked"}
        onCancel={() => navigationBlocker.reset?.()}
        onConfirm={handleConfirmPendingNavigation}
      />

      <Snackbar
        open={toast.open || Boolean(successMessage)}
        autoHideDuration={3500}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeToast}
          severity={successMessage ? "success" : toast.severity}
          sx={{ width: "100%", borderRadius: "14px" }}
        >
          {successMessage || toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
